<template>
  <div class="stats-panel">
    <div class="panel-head" :style="{ borderTopColor: accent }">
      <div class="panel-label">
        <span class="panel-dot" :style="{ background: accent }"></span>
        {{ summaryLabel }}
      </div>
      <button v-if="removable" class="panel-remove" @click="$emit('remove')" title="비교 제거">&times;</button>
    </div>

    <!-- 필터 -->
    <form class="panel-filter" @submit.prevent="run">
      <div class="pf-row">
        <label>자치구</label>
        <select v-model="f.sggCd">
          <option value="">전체 서울</option>
          <option v-for="(name, code) in guOptions" :key="code" :value="code">{{ name }}</option>
        </select>
      </div>
      <div class="pf-row">
        <label>법정동</label>
        <input type="text" v-model="f.umdNm" placeholder="예: 전농동" />
      </div>
      <div class="pf-row pf-area">
        <label>
          전용 평형 (평)
          <span class="info-ic" tabindex="0">ⓘ
            <span class="info-pop">
              입력값은 <b>전용면적</b> 기준입니다.<br />
              · 전용 = 실제 사용 공간(발코니 제외)<br />
              · 공급 = 전용 + 공용(복도·계단 등)<br />
              분양에서 흔히 말하는 <b>‘34평’은 공급 기준</b>이고,
              <b>국평(국민평형)</b>은 전용 84㎡ ≈ <b>전용 25평</b>(공급 34평)입니다.
            </span>
          </span>
        </label>
        <div class="pf-pair">
          <input type="number" v-model="f.minPy" placeholder="최소" />
          <span>~</span>
          <input type="number" v-model="f.maxPy" placeholder="최대" />
        </div>
        <div class="area-extra">
          <span class="area-hint" v-if="areaHint">{{ areaHint }}</span>
          <div class="area-presets">
            <button type="button" @click="preset('kook')" title="국민평형 · 전용 84㎡">국평</button>
            <button type="button" @click="preset('small')">~20평</button>
            <button type="button" @click="preset('mid')">25~32평</button>
            <button type="button" @click="preset('big')">40평~</button>
          </div>
        </div>
      </div>
      <div class="pf-row pf-span2">
        <label>기간</label>
        <div class="pf-pair">
          <input type="date" v-model="f.from" />
          <span>~</span>
          <input type="date" v-model="f.to" />
        </div>
      </div>
      <div class="pf-row">
        <label>집계 단위</label>
        <div class="pf-seg">
          <button type="button" :class="{ on: f.group === 'MONTH' }" @click="f.group = 'MONTH'">월별</button>
          <button type="button" :class="{ on: f.group === 'YEAR' }" @click="f.group = 'YEAR'">연도별</button>
        </div>
      </div>
      <button type="submit" class="pf-run" :disabled="loading">{{ loading ? '집계 중…' : '조회' }}</button>
    </form>

    <!-- 본문 -->
    <div class="panel-body">
      <!-- 스켈레톤 -->
      <template v-if="loading">
        <div class="stat-grid">
          <div class="stat-card sk" v-for="n in 6" :key="'sk-' + n">
            <div class="sk-line sk-sm"></div>
            <div class="sk-line sk-lg"></div>
          </div>
        </div>
        <div class="chart-box"><div class="sk-block"></div></div>
        <div class="dist-box"><div class="sk-block sk-dist"></div></div>
      </template>

      <template v-else-if="loaded">
        <div v-if="truncated" class="trunc-note">
          <template v-if="seriesMode">
            거래 건수·평균가·추이는 <b>전체 기간</b> 기준입니다. 중앙값·평단가·최고/최저·평형분포는 최근 {{ sampleCount.toLocaleString() }}건 표본 기준이며, 조건을 좁히면 전 구간이 반영됩니다.
          </template>
          <template v-else>
            표본 상한({{ MAX.toLocaleString() }}건)에 도달해 최근 거래 위주로 집계됐습니다. 기간·조건을 좁히면 더 정확합니다.
          </template>
        </div>
        <div v-if="stats.count === 0" class="empty-hint">조건에 맞는 거래가 없습니다.</div>

        <template v-else>
          <!-- 통계 카드 -->
          <div class="stat-grid">
            <div class="stat-card" v-for="m in visibleMetrics" :key="m.key">
              <div class="stat-label">{{ m.label }}</div>
              <div class="stat-value" :class="{ accent: m.accent }">{{ m.value }}</div>
              <div class="stat-sub" v-if="m.sub">{{ m.sub }}</div>
              <div class="stat-delta" :class="m.deltaDir" v-if="m.delta">
                <span class="dl-tag">좌 대비</span> {{ m.delta }}
              </div>
            </div>
          </div>

          <!-- 추이 차트 -->
          <div class="chart-block">
            <div class="chart-cap">
              거래량 · 평균가 추이
              <span class="cap-hint">— 실선: 평균가·거래량 / 파선: 최고~최저 범위 / 점선: 중앙값. 표시 항목을 켜고 끄면 반영됩니다.</span>
            </div>
            <div class="chart-box">
              <LineChart v-if="chartData" :data="chartData" :options="chartOptions" />
            </div>
          </div>

          <!-- 평형 분포 -->
          <div class="dist-block">
            <div class="chart-cap">평형별 거래 비중</div>
            <div class="dist-list">
              <div class="dist-row" v-for="d in dist" :key="d.band">
                <span class="dist-band">{{ d.band }}</span>
                <div class="dist-bar-wrap">
                  <div class="dist-bar" :style="{ width: d.ratio + '%', background: accent }"></div>
                </div>
                <span class="dist-val">{{ d.count.toLocaleString() }}건 · {{ d.ratio.toFixed(0) }}%</span>
              </div>
            </div>
          </div>
        </template>
      </template>

      <div v-else class="empty-hint idle">조건을 설정하고 <b>조회</b>를 누르세요.</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Line } from "vue-chartjs";
import UtilMixin from "@/common/mixins/UtilMixin";
import { SeoulTradeStatRequest } from "../class";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
} from "chart.js";

ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement, Filler);

const AMOUNT_COLOR = "#4a9fe0";
const AMOUNT_FILL = "rgba(74, 159, 224, 0.14)";
const PP_COLOR = "#e0a53a";
const TICK = "#8a9099";
const GRID = "rgba(128, 128, 128, 0.14)";
const PY = 3.3058;

export default defineComponent({
  name: "StatsPanel",
  components: { LineChart: Line },
  mixins: [UtilMixin],
  props: {
    guOptions: { type: Object as () => Record<string, string>, required: true },
    metrics: { type: Object as () => Record<string, boolean>, required: true },
    accent: { type: String, default: "#4a9fe0" },
    removable: { type: Boolean, default: false },
    initial: { type: Object, default: () => ({}) },
    compareBase: { type: Object as () => Record<string, number> | null, default: null },
  },
  data() {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return {
      MAX: 12000,
      f: {
        sggCd: "",
        umdNm: "",
        minPy: "" as string | number,
        maxPy: "" as string | number,
        from: fmt(from),
        to: fmt(now),
        group: "MONTH" as "MONTH" | "YEAR",
        ...(this.initial as any),
      },
      applied: null as any,
      loading: false,
      loaded: false,
      truncated: false,
      seriesMode: false,
      sampleCount: 0,
      trendCache: null as any,
      stats: { count: 0, avg: 0, median: 0, max: 0, min: 0, perPyeong: 0, areaM2: 0 },
      dist: [] as { band: string; count: number; ratio: number }[],
      chartData: null as any,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "top" as const, labels: { color: TICK, usePointStyle: false, boxWidth: 28, padding: 12 } },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const l = ctx.dataset.label || "";
                const v = ctx.raw;
                if (v == null) return undefined;
                if (l.includes("거래량")) return `${l}: ${Number(v).toLocaleString()}건`;
                return `${l}: ${v}억`;
              },
            },
          },
        },
        scales: {
          x: { grid: { color: GRID }, ticks: { color: TICK, maxRotation: 0, autoSkipPadding: 10 } },
          y1: { type: "linear", position: "left", grid: { color: GRID }, ticks: { color: TICK, callback: (v: any) => `${v}억` } },
          y2: { type: "linear", position: "right", grid: { drawOnChartArea: false }, ticks: { color: TICK } },
        },
      } as any,
    };
  },
  computed: {
    areaHint(): string {
      const min = Number(this.f.minPy);
      const max = Number(this.f.maxPy);
      const toM2 = (p: number) => Math.round(p * PY);
      if (min && max) return `전용 ${toM2(min)}~${toM2(max)}㎡`;
      if (min) return `전용 ${toM2(min)}㎡ 이상`;
      if (max) return `전용 ${toM2(max)}㎡ 이하`;
      return "";
    },
    summaryLabel(): string {
      const a = this.applied;
      if (!a) return "새 조건";
      const gu = a.sggCd ? this.guOptions[a.sggCd] : "서울 전체";
      const dong = a.umdNm ? ` ${a.umdNm}` : "";
      let py = "전체 평형";
      if (a.minPy && a.maxPy) py = `${a.minPy}~${a.maxPy}평`;
      else if (a.minPy) py = `${a.minPy}평 이상`;
      else if (a.maxPy) py = `${a.maxPy}평 이하`;
      return `${gu}${dong} · ${py}`;
    },
    visibleMetrics(): any[] {
      const s = this.stats;
      const base = this.compareBase;
      const areaPy = s.areaM2 ? (s.areaM2 / PY).toFixed(1) : "-";
      const sign = (d: number) => (d > 0 ? "▲ " : d < 0 ? "▼ " : "± ");
      const eok = (d: number) => `${sign(d)}${Math.abs(d).toFixed(2)}억`;
      const cnt = (d: number) => `${sign(d)}${Math.abs(Math.round(d)).toLocaleString()}건`;
      const won = (d: number) => `${sign(d)}${Math.abs(Math.round(d)).toLocaleString()}`;
      const m2 = (d: number) => `${sign(d)}${Math.abs(d).toFixed(1)}㎡`;
      const all = [
        { key: "count", sk: "count", label: "거래 건수", value: `${s.count.toLocaleString()}건`, num: s.count, fmt: cnt, accent: false },
        { key: "avg", sk: "avg", label: "평균 거래가", value: `${s.avg.toFixed(2)}억`, num: s.avg, fmt: eok, accent: true },
        { key: "median", sk: "median", label: "중앙값", value: `${s.median.toFixed(2)}억`, sub: "가격대 중간값", num: s.median, fmt: eok, accent: false },
        { key: "perPyeong", sk: "perPyeong", label: "평단가", value: `${Math.round(s.perPyeong).toLocaleString()}`, sub: "만원 / 평", num: s.perPyeong, fmt: won, accent: true },
        { key: "max", sk: "max", label: "최고 거래가", value: `${s.max.toFixed(2)}억`, num: s.max, fmt: eok, accent: false },
        { key: "min", sk: "min", label: "최저 거래가", value: `${s.min.toFixed(2)}억`, num: s.min, fmt: eok, accent: false },
        { key: "area", sk: "areaM2", label: "평균 전용면적", value: `${s.areaM2.toFixed(1)}㎡`, sub: `${areaPy}평`, num: s.areaM2, fmt: m2, accent: false },
      ];
      return all
        .filter((m) => this.metrics[m.key])
        .map((m) => {
          if (base && typeof base[m.sk] === "number") {
            const d = m.num - base[m.sk];
            return { ...m, delta: m.fmt(d), deltaDir: d > 0 ? "up" : d < 0 ? "down" : "flat" };
          }
          return m;
        });
    },
  },
  methods: {
    preset(kind: string) {
      if (kind === "kook") { this.f.minPy = 24; this.f.maxPy = 27; }
      else if (kind === "small") { this.f.minPy = ""; this.f.maxPy = 20; }
      else if (kind === "mid") { this.f.minPy = 25; this.f.maxPy = 32; }
      else if (kind === "big") { this.f.minPy = 40; this.f.maxPy = ""; }
    },
    async run() {
      this.loading = true;
      const filter = { ...this.f };
      try {
        const minM2 = filter.minPy ? Number(filter.minPy) * PY : "";
        const maxM2 = filter.maxPy ? Number(filter.maxPy) * PY : "";
        const base: any = {
          sggCd: filter.sggCd,
          umdNm: filter.umdNm,
          minExcluUseAr: minM2,
          maxExcluUseAr: maxM2,
          dealDateFrom: filter.from,
          dealDateTo: filter.to,
        };
        Object.keys(base).forEach((k) => {
          if (base[k] === "" || base[k] === null || base[k] === undefined) delete base[k];
        });

        const rows = await this.fetchAll(base);
        this.sampleCount = rows.all.length;

        // 광범위 조건(표본 상한 초과)은 서버 집계로 건수·평균가·추이를 정확히 보정
        let series: any[] | null = null;
        if (rows.truncated) {
          try {
            const req = new SeoulTradeStatRequest().bind({
              sggCd: filter.sggCd,
              minExcluUseAr: minM2 || "",
              maxExcluUseAr: maxM2 || "",
              startYmd: filter.from.replace(/-/g, ""),
              endYmd: filter.to.replace(/-/g, ""),
              groupBy: filter.group,
            });
            const res = await this.getApi().apt().getSeoulTradeStats(req);
            if (Array.isArray(res)) series = res;
          } catch {
            series = null;
          }
        }

        this.aggregate(rows.all, filter.group, series);
        this.truncated = rows.truncated;
        this.applied = { ...filter };
        this.loaded = true;
        this.$emit("stats", { ...this.stats });
      } catch (e) {
        this.stats = { count: 0, avg: 0, median: 0, max: 0, min: 0, perPyeong: 0, areaM2: 0 };
        this.loaded = true;
        this.$emit("stats", { ...this.stats });
      } finally {
        this.loading = false;
      }
    },
    async fetchAll(base: any) {
      const first = await this.getApi().apt().getAptTrade({ ...base, page: 0, size: 1000 });
      const all: any[] = [...(first.content || [])];
      const totalPages = first.totalPages || 1;
      const pageSize = (first.content && first.content.length) || 1000;
      const maxPages = Math.min(totalPages, Math.max(1, Math.ceil(this.MAX / Math.max(pageSize, 1))));

      const reqs = [];
      for (let p = 1; p < maxPages; p++) {
        reqs.push(this.getApi().apt().getAptTrade({ ...base, page: p, size: 1000 }));
      }
      const rest = await Promise.all(reqs);
      rest.forEach((r: any) => all.push(...(r.content || [])));

      return { all, truncated: totalPages > maxPages };
    },
    aggregate(rows: any[], group: "MONTH" | "YEAR", series: any[] | null = null) {
      // 해제 거래 제외
      const data = rows.filter((r) => r.cdealType !== "O" && r.dealAmount > 0 && r.excluUseAr > 0);
      const amounts = data.map((r) => r.dealAmount); // 만원
      const count = amounts.length;

      if (count === 0) {
        this.stats = { count: 0, avg: 0, median: 0, max: 0, min: 0, perPyeong: 0, areaM2: 0 };
        this.dist = [];
        this.chartData = null;
        return;
      }

      const sum = amounts.reduce((a, b) => a + b, 0);
      const sorted = [...amounts].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
      const perPyeongs = data.map((r) => r.dealAmount / (r.excluUseAr / PY)); // 만원/평
      const areaSum = data.reduce((a, r) => a + r.excluUseAr, 0);

      // 서버 집계(series)가 있으면 건수·평균가는 전체 기간 정확값으로 대체
      let headCount = count;
      let headAvg = sum / count / 10000;
      if (series && series.length) {
        const totCnt = series.reduce((a, s) => a + (s.totalCount || 0), 0);
        let wAmt = 0;
        let wCnt = 0;
        series.forEach((s) => {
          if (s.totalAvgAmount && s.totalCount) {
            wAmt += s.totalAvgAmount * s.totalCount;
            wCnt += s.totalCount;
          }
        });
        if (totCnt > 0) headCount = totCnt;
        if (wCnt > 0) headAvg = wAmt / wCnt;
      }

      this.stats = {
        count: headCount,
        avg: headAvg,
        median: median / 10000,
        max: sorted[sorted.length - 1] / 10000,
        min: sorted[0] / 10000,
        perPyeong: perPyeongs.reduce((a, b) => a + b, 0) / count,
        areaM2: areaSum / count,
      };

      // 평형 분포
      const bands = [
        { band: "~20평", min: 0, max: 20 },
        { band: "20~30평", min: 20, max: 30 },
        { band: "30~40평", min: 30, max: 40 },
        { band: "40~50평", min: 40, max: 50 },
        { band: "50평~", min: 50, max: 9999 },
      ];
      this.dist = bands
        .map((b) => {
          const c = data.filter((r) => {
            const py = r.excluUseAr / PY;
            return py >= b.min && py < b.max;
          }).length;
          return { band: b.band, count: c, ratio: (c / count) * 100 };
        })
        .filter((b) => b.count > 0);

      // ===== 추이: 틱별 평균/거래량/최고~최저/중앙값 =====
      this.seriesMode = !!(series && series.length);
      const keyOf = (y: number, m: number) =>
        group === "YEAR" ? `${y}` : `${y}-${String(m).padStart(2, "0")}`;

      // 원시 표본 기반 틱별 통계(최고·최저·중앙값)
      const rawB = new Map<string, { amts: number[]; sum: number }>();
      data.forEach((r) => {
        const k = keyOf(r.dealYear, r.dealMonth);
        const b = rawB.get(k) || { amts: [], sum: 0 };
        b.amts.push(r.dealAmount);
        b.sum += r.dealAmount;
        rawB.set(k, b);
      });
      const rawStat = new Map<string, any>();
      rawB.forEach((b, k) => {
        const s = [...b.amts].sort((x, y) => x - y);
        const m = Math.floor(s.length / 2);
        rawStat.set(k, {
          count: s.length,
          avg: b.sum / s.length / 10000,
          min: s[0] / 10000,
          max: s[s.length - 1] / 10000,
          median: (s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2) / 10000,
        });
      });

      // 라벨 + 평균/거래량 (series 있으면 전체 기간 정확값)
      let labels: string[];
      let avgArr: (number | null)[];
      let countArr: (number | null)[];
      if (series && series.length) {
        const ss = [...series].sort(
          (a, b) => (a.year * 100 + (a.month || 0)) - (b.year * 100 + (b.month || 0))
        );
        labels = ss.map((s) => keyOf(s.year, s.month));
        avgArr = ss.map((s) => Number((s.totalAvgAmount || 0).toFixed(2)));
        countArr = ss.map((s) => s.totalCount || 0);
      } else {
        labels = [...rawStat.keys()].sort();
        avgArr = labels.map((k) => Number(rawStat.get(k).avg.toFixed(2)));
        countArr = labels.map((k) => rawStat.get(k).count);
      }
      // 광범위(표본) 조건은 틱별 최고/최저가 최근 표본만이라 축을 왜곡 → 범위 미표시
      const showRange = !this.seriesMode;
      const maxArr = labels.map((k) => (showRange && rawStat.has(k) ? Number(rawStat.get(k).max.toFixed(2)) : null));
      const minArr = labels.map((k) => (showRange && rawStat.has(k) ? Number(rawStat.get(k).min.toFixed(2)) : null));
      const medArr = labels.map((k) => (showRange && rawStat.has(k) ? Number(rawStat.get(k).median.toFixed(2)) : null));

      this.trendCache = { labels, avgArr, countArr, maxArr, minArr, medArr };
      this.buildChart();
    },

    buildChart() {
      const c = this.trendCache;
      if (!c) { this.chartData = null; return; }
      const M = this.metrics;
      const ds: any[] = [];
      const hasRange = c.maxArr.some((v: number | null) => v != null);
      const hasMed = c.medArr.some((v: number | null) => v != null);

      // 최고~최저 밴드 (파선 + 음영) — 구체 조건에서 표시
      if (hasRange && M.max && M.min) {
        ds.push({ label: "최고가", data: c.maxArr, borderColor: "rgba(74,159,224,0.55)", borderWidth: 1.5, borderDash: [4, 3], pointRadius: 0, pointHoverRadius: 4, fill: false, tension: 0.35, spanGaps: false, yAxisID: "y1" });
        ds.push({ label: "최저가", data: c.minArr, borderColor: "rgba(74,159,224,0.55)", borderWidth: 1.5, borderDash: [4, 3], pointRadius: 0, pointHoverRadius: 4, fill: "-1", backgroundColor: "rgba(74,159,224,0.09)", tension: 0.35, spanGaps: false, yAxisID: "y1" });
      } else if (hasRange && M.max) {
        ds.push({ label: "최고가", data: c.maxArr, borderColor: "rgba(74,159,224,0.6)", borderWidth: 1.5, borderDash: [4, 3], pointRadius: 0, fill: false, tension: 0.35, spanGaps: false, yAxisID: "y1" });
      } else if (hasRange && M.min) {
        ds.push({ label: "최저가", data: c.minArr, borderColor: "rgba(74,159,224,0.6)", borderWidth: 1.5, borderDash: [4, 3], pointRadius: 0, fill: false, tension: 0.35, spanGaps: false, yAxisID: "y1" });
      }
      // 중앙값 (점선)
      if (hasMed && M.median) {
        ds.push({ label: "중앙값", data: c.medArr, borderColor: "#9aa0ab", borderWidth: 1.5, borderDash: [2, 3], pointRadius: 0, pointHoverRadius: 4, fill: false, tension: 0.4, spanGaps: false, yAxisID: "y1" });
      }
      // 평균가 (실선)
      if (M.avg) {
        ds.push({ label: "평균가", data: c.avgArr, borderColor: AMOUNT_COLOR, backgroundColor: AMOUNT_FILL, borderWidth: 2.5, borderDash: [], pointRadius: 0, pointHoverRadius: 5, fill: false, tension: 0.4, cubicInterpolationMode: "monotone", yAxisID: "y1" });
      }
      // 거래량 (실선)
      if (M.count) {
        ds.push({ label: "거래량", data: c.countArr, borderColor: PP_COLOR, borderWidth: 2, borderDash: [], pointRadius: 0, pointHoverRadius: 5, fill: false, tension: 0.4, cubicInterpolationMode: "monotone", yAxisID: "y2" });
      }
      this.chartData = { labels: c.labels, datasets: ds };
    },
  },
  watch: {
    metrics: {
      handler() {
        if (this.trendCache) this.buildChart();
      },
      deep: true,
    },
  },
  mounted() {
    this.run();
  },
});
</script>
